import { Box, useTheme } from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';

import {
  useAppDispatch,
  useAppSelector,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '@verantos-dev/core-annotation-tool';
import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { useEncounterStatisticsDatagridFetch } from '../../hook/useEncounterStatsDataGridFetch';
import { setEncounterStatsTablePaginationModel } from '../../state/metricsSlice';
import {
  selectEncounterStatsTablePage,
  selectEncounterStatsTablePageSize,
} from '../../state/selector';
import { EncounterStatsItem } from '../../types/responseTypes';
import { DatagridExportToolbar } from '../DatagridExportToolbar';

const nullToZero = (value: unknown) => (value == null ? 0 : value);

// Some numeric fields carry the literal string 'NA' instead of a number (see
// metrics_service.py) which breaks MUI's default numeric sort comparator
// (Number('NA') is NaN, so Array.sort treats every comparison as a no-op).
const numericOrNAComparator = (v1: number | string, v2: number | string) => {
  const n1 = typeof v1 === 'number' ? v1 : Infinity;
  const n2 = typeof v2 === 'number' ? v2 : Infinity;
  return n1 - n2;
};

export const EncounterStatisticsTable = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const encounterStatsTablePage = useAppSelector(selectEncounterStatsTablePage);
  const encounterStatsTablePageSize = useAppSelector(selectEncounterStatsTablePageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setEncounterStatsTablePaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const { isFetching, data } = useEncounterStatisticsDatagridFetch();
  useEffect(() => {
    if (isFetching) {
      dispatch(
        setEncounterStatsTablePaginationModel({ page: 0, pageSize: encounterStatsTablePageSize })
      );
    }
  }, [isFetching]);

  const rows = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((row: EncounterStatsItem) => ({
      ...row.annotators,
      ...row,
    }));
  }, [data]);

  const annotatorColumns: GridColDef[] = useMemo(() => {
    if (!data?.data?.length) return [];

    const annotatorSet = new Set<string>();

    data.data.forEach((row: EncounterStatsItem) => {
      Object.keys(row.annotators || {}).forEach((name) => {
        annotatorSet.add(name);
      });
    });

    return Array.from(annotatorSet).map((name) => ({
      field: name,
      headerName: name,
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: nullToZero,
    }));
  }, [data]);

  const columns: GridColDef<EncounterStatsItem>[] = useMemo(
    () => [
      {
        field: 'srNo',
        headerName: 'Sr No.',
        sortable: false,
        valueGetter: (_value: unknown, row: EncounterStatsItem, _column, apiRef) =>
          apiRef.current.getRowIndexRelativeToVisibleRows(row.id) + 1,
        width: 100,
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 140,
        valueFormatter: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
      },

      ...annotatorColumns,

      {
        field: 'dayWiseNotes',
        headerName: 'Day wise encounters',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'totalNotes',
        headerName: 'Total encounters',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'averageVelocity',
        headerName: 'Average velocity',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'totalUniqueNotes',
        headerName: 'Total unique encounters',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'percentCompleted',
        headerName: 'Percent completed',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => `${(value ?? 0).toFixed(2)}%`,
      },
      {
        field: 'nonConflictingNotesPerDay',
        headerName: 'Non conflicting encounters per day',
        width: 220,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'totalNonConflictingNotesTillDate',
        headerName: 'Total non conflicting encounters till date',
        width: 260,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'avgNotesLast5Days',
        headerName: 'Avg encounters (last 5 days)',
        width: 200,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
        sortComparator: numericOrNAComparator,
      },
      {
        field: 'totalNotesLeft',
        headerName: 'Total encounters left',
        width: 160,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
        sortComparator: numericOrNAComparator,
      },
      {
        field: 'projectedDaysLeft',
        headerName: 'Projected days left',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number, row: EncounterStatsItem) =>
          row.projectedEndDate === 'NA' || !row.projectedEndDate ? 'NA' : value ?? 0,
        sortComparator: numericOrNAComparator,
      },
      {
        field: 'projectedEndDate',
        headerName: 'Projected end date',
        width: 180,
        valueFormatter: (value: string) => {
          if (value === 'completed') {
            return 'completed';
          }
          if (value === 'NA' || !value) {
            return 'NA';
          }
          return dayjs(value).format('MMM D, YYYY');
        },
      },
      {
        field: 'dailyInitialF1Measure',
        headerName: 'Daily initial F1',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'dailyCurrentF1Measure',
        headerName: 'Daily current F1',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'overallInitialF1Measure',
        headerName: 'Overall initial F1',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
      {
        field: 'overallCurrentF1Measure',
        headerName: 'Overall current F1',
        width: 180,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: nullToZero,
      },
    ],
    [annotatorColumns]
  );

  return (
    <Box>
      <DataGridLayout id="encounter-statistics-data-grid">
        <DataGrid
          id="encounter-statistics-data-grid"
          mode="client"
          pagination
          loadOnFetch
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          paginationModel={{ page: encounterStatsTablePage, pageSize: encounterStatsTablePageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSearchParamSync
          preventUrlReadWhenSyncDisabled
          useFetchDataGridData={useEncounterStatisticsDatagridFetch}
          getRowId={(row) => row.id}
          columns={columns}
          rows={rows}
          slots={{ toolbar: DatagridExportToolbar }}
          slotProps={{
            toolbar: {
              fileName: 'encounter-statistics',
            },
          }}
          initialState={{
            pinnedColumns: {
              left: ['srNo', 'date'],
            },
          }}
          sx={{
            maxHeight: '70vh',

            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: '1.2',
              textAlign: 'center',
            },

            '& .MuiDataGrid-columnHeader': {
              height: 'auto !important',
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
